/* Account-first Scout. Only short-lived, non-PII criteria survive native sign-in. */
(() => {
  'use strict';
  if (customElements.get('thryft-scout')) return;
  const SESSION_AGE = 14 * 60 * 1000;
  const EXPIRED_SESSION = 'Your session expired. Refresh Scout and try again.';
  const SCOPES = ['work', 'edition', 'variant', 'release', 'master'];

  class Scout extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.config = JSON.parse(this.querySelector('[data-scout-config]').textContent);
      this.preview = this.dataset.mode !== 'live';
      this.dialog = this.q('dialog');
      this.form = this.q('[data-scout-form]');
      this.events = new AbortController();
      this.requestId = this.uncertainCreate?.payload.request_id || crypto.randomUUID();
      this.revision = (this.revision || 0) + 1;
      this.context = null;
      this.ready = false;
      this.qAll('[data-scout-preview]').forEach(el => { el.hidden = !this.preview; });
      this.on(this.q('[data-scout-open]'), 'click', () => this.searchEntry ? this.checkSearchEntry() : this.open());
      this.on(this.q('[data-scout-close]'), 'click', () => this.dialog.close());
      this.on(this.q('[data-scout-stop]'), 'click', () => this.stop());
      this.on(this.dialog, 'keydown', event => this.keepFocus(event));
      this.on(this.dialog, 'close', () => {
        this.revision += 1;
        this.lookup?.abort();
        this.renderEntry();
        if (!this.hidden) this.q('[data-scout-open]').focus();
        else {
          const product = this.closest('[data-section-type="product"]');
          const heading = product?.querySelector('h1');
          const target = heading?.getClientRects().length ? heading : product;
          if (target) { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }
        }
      });
      this.on(this.form, 'submit', event => this.submit(event));
      this.on(document, 'visitorConsentCollected', () => this.renderAccount());
      this.on(document, 'scout:privacy-ready', () => this.renderAccount());
      this.on(document, 'scout:privacy-error', event => this.say(event.detail));
      this.on(this.form, 'change', () => this.updatePreferences());
      this.on(this.form.elements.max_price, 'input', () => this.updatePreferences());
      this.on(document, 'product:variant-change', event => this.changeVariant(event.detail?.variant));
      this.on(this.q('[data-scout-retry]'), 'click', () => { if (!this.submitting) this.refresh(); });
      this.renderEntry();
      this.consumeSearchCue();
      this.restoreIntent();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.lookup?.abort();
      this.stockLookup?.abort();

      this.events = null;
    }

    q(selector) { return this.querySelector(selector); }
    qAll(selector) { return [...this.querySelectorAll(selector)]; }
    on(element, event, callback) { element.addEventListener(event, callback, { signal: this.events.signal }); }
    t(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), this.config.copy[key] || key);
    }
    say(text) { this.q('[data-scout-status]').textContent = text; }
    phase(name, text = '') {
      this.dataset.phase = name;
      const busy = ['loading', 'saving', 'stopping'].includes(name);
      this.dialog.setAttribute('aria-busy', String(busy));
      this.form.inert = busy;
      this.say(text);
    }
    node(tag, text, parent) {
      const node = document.createElement(tag);
      if (text) node.textContent = text;
      if (parent) parent.append(node);
      return node;
    }

    keepFocus(event) {
      if (event.key !== 'Tab' || !this.dialog.open || event.defaultPrevented) return;
      const controls = [...this.dialog.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')].filter(el => {
        const closedDetails = el.closest('details:not([open])');
        if (closedDetails && !(el === closedDetails.querySelector(':scope > summary') && !closedDetails.parentElement.closest('details:not([open])'))) return false;
        return el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[hidden],[inert],[aria-hidden="true"]') && el.getClientRects().length && !['hidden', 'collapse'].includes(this.dialog.ownerDocument.defaultView.getComputedStyle(el).visibility);
      });
      const active = this.dialog.ownerDocument.activeElement;
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) {
        event.preventDefault();
        this.q('h2').focus({ preventScroll: true });
      } else if (event.shiftKey && active === first || !event.shiftKey && active === last) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    consumeSearchCue() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('scout') !== 'open') return;
      url.searchParams.delete('scout');
      if (url.hash === '#scout') url.hash = '';
      window.history.replaceState(window.history.state, '', url);
      this.searchEntry = true;
      this.checkSearchEntry();
    }

    async checkSearchEntry() {
      if (this.stockChecking || this.config.country !== 'SG' || this.config.currency !== 'SGD') return;
      this.stockChecking = true;
      const button = this.q('[data-scout-open]');
      const status = this.q('[data-scout-entry-status]');
      button.disabled = true;
      status.textContent = this.t('checking');
      this.stockLookup = new AbortController();
      const timeout = setTimeout(() => this.stockLookup.abort(), 8000);
      try {
        // This read is also required in preview; only Scout writes are simulated.
        const response = await fetch(this.config.product_url + '.js', {
          cache: 'no-store', credentials: 'same-origin', signal: this.stockLookup.signal
        });
        if (!response.ok) throw Error('stock');
        const product = await response.json();
        if (String(product.id) !== this.config.product_id || !product.variants?.length
          || product.variants.some(variant => typeof variant.available !== 'boolean')) throw Error('stock');
        if (!this.isConnected) return;
        this.config.variants = product.variants.map(variant => ({ id: String(variant.id), available: variant.available }));
        if (!this.config.variants.some(variant => variant.id === this.config.variant_id)) {
          this.config.variant_id = this.config.variants[0].id;
          this.context = null;
        }
        this.searchEntry = false;
        status.textContent = '';
        this.renderEntry();
        if (this.config.variants.some(variant => variant.available)) {
          const productSection = this.closest('[data-section-type="product"]');
          const purchase = [...(productSection?.querySelectorAll('[data-add-to-cart]:not([disabled])') || [])]
            .find(element => element.getClientRects().length);
          if (purchase) purchase.focus();
          // The server-rendered form may still be sold out. Reload once with the
          // cue already removed so Shopify restores working purchase controls.
          else window.location.reload();
          return;
        }
        this.open();
      } catch (error) {
        if (this.isConnected) status.textContent = this.t('stock_check_failed');
      } finally {
        clearTimeout(timeout);
        this.stockChecking = false;
        button.disabled = false;
      }
    }

    open() {
      // The theme popup lifecycle honors this page-local flag, including after
      // closing Scout. No newsletter permission or persistence is changed.
      document.documentElement.setAttribute('data-scout-engaged', '');
      document.dispatchEvent(new CustomEvent('scout:open'));
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      if (this.account) this.renderAccount();
      if (!this.submitting && !this.uncertainCreate && ((!this.ready && !this.savedWatch) || this.sessionStale())) this.refresh({ review: this.sessionStale() });
      this.q('h2').focus({ preventScroll: true });
    }

    api(path, data, signal = this.events?.signal) {
      return window.ThryftScoutAccount.api(path, data, signal);
    }

    criteria() {
      const values = Object.fromEntries(new FormData(this.form));
      const scope = values.scope || (this.intent ? this.intent.criteria.scope : this.context?.default_scope || (this.config.category === 'vinyl' ? 'variant' : 'edition'));
      return {
        scope, max_price: values.max_price || '',
        condition: this.config.category === 'book' ? values.condition || '' : '',
        media_condition: this.config.category === 'vinyl' && scope !== 'variant' ? values.media_condition || '' : '',
        sleeve_condition: this.config.category === 'vinyl' && scope !== 'variant' ? values.sleeve_condition || '' : ''
      };
    }

    validPrice() {
      const field = this.form.elements.max_price;
      const valid = field.validity.valid && (!field.value || /^\d+(?:\.\d{1,2})?$/.test(field.value) && Number(field.value) > 0);
      this.q('[data-scout-price-error]').textContent = valid ? '' : this.t('price_invalid');
      field.setAttribute('aria-invalid', String(!valid));
      return valid;
    }

    updatePreferences() {
      if (this.uncertainCreate) return;
      if (this.intent) {
        this.intent.remember_interest = this.form.elements.remember_interest.checked;
        const criteria = this.criteria();
        if (this.context?.available_scopes?.includes(Object.fromEntries(new FormData(this.form)).scope)) {
          this.intent = null;
          this.say('');
        } else this.intent.criteria = criteria;
      }
      this.requestId = crypto.randomUUID();
      this.q('[data-scout-vinyl-conditions]').hidden = this.config.category !== 'vinyl' || this.criteria().scope === 'variant';
      this.summary();
      this.q('[data-scout-submit]').disabled = !this.ready || !!this.intent || !this.validPrice();
    }

    changeVariant(variant) {
      const known = this.config.variants.find(item => item.id === String(variant?.id));
      if (!known) return;
      if (typeof variant.available === 'boolean') known.available = variant.available;
      this.renderEntry();
      // Pending verification retains the submitted identity and preferences.
      if (this.savedWatch || this.submitting || this.uncertainCreate || this.config.variant_id === known.id) return;
      this.config.variant_id = known.id;
      this.requestId = crypto.randomUUID();
      this.context = null;
      this.ready = false;
      this.revision += 1;
      this.lookup?.abort();
      this.q('[data-scout-submit]').disabled = true;
      if (this.dialog.open) this.refresh();
    }

    fixture() {
      const vinyl = this.config.category === 'vinyl';
      return {
        source: { title: this.config.title, currency: 'SGD' },
        available_scopes: vinyl ? ['release', 'master', 'variant'] : ['work', 'edition'],
        default_scope: vinyl ? 'release' : 'work'
      };
    }

    sessionStale() { return !!this.accountFetchedAt && Date.now() - this.accountFetchedAt >= SESSION_AGE; }

    async refresh({ review = false } = {}) {
      if (this.submitting || this.uncertainCreate || !this.isConnected) return;
      const requestId = this.requestId;
      const chosen = this.context ? { ...this.criteria() } : null;
      const remember = this.intent?.remember_interest ?? this.form.elements.remember_interest.checked;
      const revision = ++this.revision;
      this.lookup?.abort();
      this.lookup = new AbortController();
      this.ready = false;
      this.q('[data-scout-submit]').disabled = true;
      this.q('[data-scout-retry]').hidden = true;
      if (this.config.country !== 'SG' || this.config.currency !== 'SGD') {
        this.say(this.t('unsupported'));
        this.renderEntry();
        return;
      }
      this.phase('loading', this.t('checking'));
      try {
        const query = new URLSearchParams({ product_id: this.config.product_id, variant_id: this.config.variant_id, country: this.config.country, include_matches: 'false', session: 'true' });
        const context = this.preview ? { ...this.fixture(), authenticated: false } : await this.api('/context?' + query, null, this.lookup.signal);
        if (revision !== this.revision || !this.isConnected) return;
        this.reviewDenied = false;
        this.form.hidden = !!this.savedWatch;
        this.account = context;
        this.accountFetchedAt = Date.now();
        if (!this.preview && !this.account.authenticated) {
          this.context = null;
          this.q('[data-scout-options]').replaceChildren();
          this.q('[data-scout-scopes]').hidden = true;
          this.q('.scout__limits').hidden = true;
          this.q('.scout__permission').hidden = true;
          this.q('[data-scout-source]').textContent = '';
          this.renderAccount();
          this.q('[data-scout-submit]').disabled = false;
          this.phase('ready');
          return;
        }
        const scopes = Array.isArray(context.available_scopes) ? [...new Set(context.available_scopes.filter(scope => SCOPES.includes(scope)))] : [];
        this.context = { ...context, available_scopes: scopes };
        this.q('[data-scout-scopes]').hidden = false;
        this.q('.scout__limits').hidden = false;
        this.q('.scout__permission').hidden = false;
        if (revision !== this.revision || !this.isConnected) return;
        const chosenScope = chosen?.scope || this.form.elements.scope?.value;
        if (chosen && !scopes.includes(chosenScope)) this.intent = { criteria: chosen, remember_interest: remember };
        this.renderScopes(scopes, scopes.includes(chosenScope) ? chosenScope : context.default_scope);
        this.renderSource(context.source || {});
        this.applyIntent();
        this.requestId = requestId;
        this.form.elements.remember_interest.checked = remember;
        this.renderAccount();
        this.summary();
        this.ready = scopes.length > 0;
        this.q('[data-scout-submit]').disabled = !this.ready || !!this.intent || !this.validPrice();
        this.phase('ready', this.intent ? this.t('restored_scope_unavailable') : this.ready ? (review ? this.t('session_review') : '') : this.t('scope_unavailable'));
        const matches = this.intent ? [] : (context.watches || []).filter(watch => this.samePreferences(watch.criteria));
        const existing = matches.find(watch => ['active', 'paused'].includes(watch.status)) || matches[0];
        if (existing) this.result(existing);
        if (review && this.savedWatch) this.say(this.t('session_review_cancel'));
        this.renderEntry();
      } catch (error) {
        if (error.name === 'AbortError' || revision !== this.revision || !this.isConnected) return;
        const denied = !this.preview && error.status === 403 && error.message === 'Sign in to an authorized availability review session.';
        if (denied) { this.reviewDenied = true; this.context = null; }
        if (this.reviewDenied) this.renderAccount();
        this.phase('error', denied ? this.t('review_unavailable') : error.message);
        this.q('[data-scout-retry]').hidden = false;
      }
    }

    scopeLabel(scope) {
      if (!SCOPES.includes(scope)) return this.t('restored_choice');
      if (scope === 'variant' || scope === 'edition' && !this.context?.source?.isbn) return this.t('listing');
      return this.t(scope);
    }

    scopeDetail(scope) {
      if (scope === this.intent?.criteria.scope && !this.context?.available_scopes?.includes(scope)) {
        return this.t('restored_scope_unavailable_detail');
      }
      if (scope === 'work' && this.context?.breadth_status === 'pending') {
        return this.t(this.context.source?.isbn ? 'work_pending_detail' : 'work_pending_listing_detail');
      }
      const narrow = scope === 'variant' || scope === 'edition' && !this.context?.source?.isbn;
      return this.t(narrow ? 'listing_detail' : scope + '_detail');
    }

    renderScopes(scopes, selected) {
      const host = this.q('[data-scout-options]');
      const allowed = scopes.filter(scope => SCOPES.includes(scope));
      const unavailable = !!this.intent && selected === this.intent.criteria.scope && !allowed.includes(selected);
      const displayed = unavailable ? [selected, ...allowed] : allowed;
      const chosen = unavailable || allowed.includes(selected) ? selected : allowed.includes(this.context.default_scope) ? this.context.default_scope : allowed[0];
      const signature = displayed.map(scope => `${scope}:${allowed.includes(scope)}:${this.scopeLabel(scope)}:${this.scopeDetail(scope)}`).join('|');
      if (this.scopeSignature !== signature) {
        host.replaceChildren();
        this.q('[data-scout-scopes] legend').hidden = displayed.length === 1 && !unavailable;
        for (const scope of displayed) {
          if (displayed.length === 1 && !unavailable) {
            const input = this.node('input', '', host); input.type = 'hidden'; input.name = 'scope'; input.value = scope;
            this.node('strong', this.scopeLabel(scope), host);
            this.node('p', this.scopeDetail(scope), host).className = 'scout__muted';
            continue;
          }
          const label = this.node('label', '', host);
          label.className = 'scout__choice';
          const input = this.node('input', '', label);
          input.type = 'radio'; input.name = 'scope'; input.value = scope; input.disabled = !allowed.includes(scope);
          const text = this.node('span', '', label);
          this.node('strong', this.scopeLabel(scope), text);
          this.node('small', this.scopeDetail(scope), text);
        }
        this.scopeSignature = signature;
      }
      host.querySelectorAll('input').forEach(input => { input.checked = input.value === chosen; });
      this.q('[data-scout-book-condition]').hidden = this.config.category !== 'book';
      this.q('[data-scout-vinyl-conditions]').hidden = this.config.category !== 'vinyl' || chosen === 'variant';
    }

    renderSource(source) {
      const fields = this.config.category === 'book'
        ? [source.language, source.format, source.translator, source.isbn]
        : this.vinylDetails(source);
      this.q('[data-scout-source]').textContent = fields.map(value => Array.isArray(value) ? value.join(' ') : value).filter(Boolean).join(' · ');
    }

    vinylDetails(copy) {
      const gradeKeys = { M: 'grade_m', NM: 'grade_nm', E: 'grade_e', EX: 'grade_e', 'VG+': 'grade_vgp', VG: 'grade_vg', 'G+': 'grade_gp' };
      const grade = value => gradeKeys[value] ? this.t(gradeKeys[value]) : this.t('not_specified');
      return [copy.pressing || (copy.release_id ? this.t('release_identity', { id: copy.release_id }) : ''), copy.format,
        `${this.t('record_grade')}: ${grade(copy.media_condition)}`,
        `${this.t('sleeve_grade')}: ${copy.has_missing_sleeve === true ? this.t('missing_sleeve') : grade(copy.sleeve_condition)}`];
    }

    summary() {
      const criteria = this.criteria();
      const selected = name => {
        const select = this.form.elements[name];
        return select.value ? select.selectedOptions[0].textContent : '';
      };
      const conditions = this.config.category === 'vinyl'
        ? [criteria.media_condition && selected('media_condition'), criteria.sleeve_condition && selected('sleeve_condition')].filter(Boolean).join(' / ')
        : selected('condition');
      this.q('[data-scout-summary]').textContent = (conditions || this.t('any')) + ' · ' + (criteria.max_price ? window.ThryftScoutAccount.money(criteria.max_price) : this.t('no_limit'));
    }

    renderEntry() {
      const eligible = this.config.country === 'SG' && this.config.currency === 'SGD'
        && this.config.variants.length > 0 && !this.config.variants.some(variant => variant.available);
      this.q('[data-scout-banner]').hidden = !eligible;
      // Do not hide an open dialog or its saved status during stock changes.
      this.hidden = !eligible && !this.dialog.open;
    }

    renderAccount() {
      const signedIn = this.account?.authenticated === true;
      const signedOut = !this.reviewDenied && !this.preview && this.account?.authenticated === false;
      this.dataset.reviewDenied = String(!!this.reviewDenied);
      if (this.reviewDenied) this.form.hidden = true;
      this.dataset.signedOut = String(signedOut);
      this.q('[data-scout-sign-in]').hidden = !signedOut;
      this.q('[data-scout-sign-in-return]').hidden = !signedOut;
      this.q('[data-scout-email]').textContent = signedIn ? this.t('delivery_to', { email: this.account.email }) : this.t(signedOut ? 'sign_in_prompt' : 'sign_in_help');
      this.q('[data-scout-submit]').textContent = this.preview ? this.t('preview_draft') : this.t(signedIn ? (this.uncertainCreate ? 'submission_retry' : 'create') : 'sign_in_create');
      const choice = this.q('[data-scout-personalisation]');
      choice.hidden = !signedIn || window.ThryftScoutAccount.preferencesState() !== true;
    }

    samePreferences(criteria) {
      const current = this.criteria();
      if (!criteria) return false;
      if (current.scope === 'variant') {
        const variant = criteria.source?.variant_id || criteria.target?.variant_id;
        if (String(variant || '').split('/').pop() !== this.config.variant_id) return false;
      }
      const expected = this.context?.criteria;
      const requestedWork = criteria.requested_scope === 'work';
      const anchor = requestedWork ? criteria.edition_fallback || criteria : criteria;
      if (expected && ['country', 'currency', 'language', 'format'].some(key =>
        (anchor[key] || '') !== (expected[key] || ''))) return false;
      if (expected && !requestedWork) {
        const formats = value => JSON.stringify([...(value.formats || value.target?.formats || [])].sort());
        if (formats(criteria) !== formats(expected)) return false;
      }
      return criteria && ['scope', 'condition', 'max_price', 'media_condition', 'sleeve_condition'].every(key =>
        key === 'scope' ? (criteria.requested_scope || criteria.scope) === current.scope :
          key === 'max_price' && criteria[key] && current[key] ? Number(criteria[key]) === Number(current[key]) : (criteria[key] || '') === current[key]);
    }

    result(watch) {
      this.savedWatch = watch;
      this.form.hidden = true;
      const terminal = ['stopped', 'completed'].includes(watch.status);
      const requestedWork = watch.criteria?.requested_scope === 'work' || watch.criteria?.scope === 'work';
      const savedSource = watch.criteria?.source || {};
      const currentSource = this.context?.source;
      const sameSource = !!currentSource?.product_id && currentSource.product_id === savedSource.product_id;
      const samePromise = sameSource && ['work_id', 'work_external_id', 'isbn', 'language', 'format'].every(key => !savedSource[key] || savedSource[key] === currentSource[key]) && ['title', 'version', 'translator'].every(key => (savedSource[key] || '') === (currentSource[key] || ''));
      const workSupported = requestedWork && samePromise && this.context.breadth_status === 'verified';
      const message = terminal || watch.status === 'paused' ? 'request_' + watch.status : watch.delivery_hold ? 'request_held' : watch.lifecycle === 'once' ? (workSupported ? 'work_saved' : 'already_saved') : 'legacy_saved';
      this.q('[data-scout-result-title]').textContent = this.t(terminal ? message : 'request_saved');
      const pendingBreadth = !terminal && watch.status === 'active' && requestedWork && !workSupported;
      const pendingCopy = this.t(watch.criteria?.isbn ? 'work_pending_saved' : 'work_pending_listing_saved');
      this.q('[data-scout-result-message]').textContent = terminal ? '' : this.t(message) + (pendingBreadth ? ' ' + pendingCopy : '');
      this.q('[data-scout-result-message]').hidden = terminal;
      this.q('[data-scout-result-email]').textContent = this.t('delivery_to', { email: watch.delivery_email || this.account.email });
      this.q('[data-scout-stop]').hidden = !['active', 'paused'].includes(watch.status);
      this.q('[data-scout-result]').hidden = false;
      this.phase('saved');
      if (this.isConnected && this.dialog.open) this.q('[data-scout-result]').focus({ preventScroll: true });
    }

    async stop() {
      if (this.submitting || !this.savedWatch || !this.isConnected || !this.dialog.open) return;
      if (this.dataset.phase === 'loading') return;
      if (this.sessionStale()) { await this.refresh({ review: true }); return; }
      this.submitting = true;
      this.q('[data-scout-stop]').disabled = true;
      this.phase('stopping', this.t('stopping'));
      try {
        const response = await this.api('/account/watch', { contract: 'once-shelf-v1', watch_id: this.savedWatch.id,
          revision: this.savedWatch.revision, nonce: this.account.nonce, action: 'stop' });
        if (!response.watch || response.watch.status !== 'stopped') throw Error(this.t('unavailable'));
        this.result({ ...this.savedWatch, ...response.watch });
      } catch (error) {
        if (error.status === 403 && error.message === EXPIRED_SESSION) { this.accountFetchedAt = 1; }
        this.phase('error', error.message);
      }
      finally {
        this.submitting = false; this.q('[data-scout-stop]').disabled = false;
        if (this.sessionStale() && this.isConnected && this.dialog.open) await this.refresh({ review: true });
      }
    }

    restoreIntent() {
      this.intent = window.ThryftScoutAccount?.takeIntent(this.config.product_url, this.config.product_id);
      if (this.intent) {
        if (typeof this.intent.remember_interest === 'boolean') this.form.elements.remember_interest.checked = this.intent.remember_interest;
        this.config.variant_id = this.intent.variant_id;
        this.searchEntry = true;
        this.checkSearchEntry();
      }
    }

    applyIntent() {
      if (!this.intent) return;
      if (typeof this.intent.remember_interest === 'boolean') this.form.elements.remember_interest.checked = this.intent.remember_interest;
      this.intent.criteria.scope = this.intent.criteria.scope || '';
      this.renderScopes(this.context.available_scopes, this.intent.criteria.scope);
      for (const key of ['condition', 'max_price', 'media_condition', 'sleeve_condition']) {
        const field = this.form.elements[key];
        if (field) { field.value = this.intent.criteria[key] || ''; if (field.tagName === 'SELECT' && field.selectedIndex < 0) field.value = ''; }
      }
      this.updatePreferences();
    }

    async submit(event) {
      event.preventDefault();
      if (this.reviewDenied || this.savedWatch || this.submitting || !this.isConnected || !this.dialog.open) return;
      if (this.uncertainCreate && (this.uncertainCreate.held || Date.now() - this.uncertainCreate.at >= SESSION_AGE)) {
        this.phase('error', this.t('submission_hold'));
        this.q('[data-scout-submit]').disabled = true;
        return;
      }
      if (this.dataset.phase === 'loading') return;
      if (!this.uncertainCreate && this.sessionStale()) { await this.refresh({ review: true }); return; }
      if (!this.preview && this.account?.authenticated === false) {
        try { window.ThryftScoutAccount.signIn(this.config, this.intent?.criteria || this.criteria(), this.form.elements.remember_interest.checked); }
        catch (error) { this.say(error.message); }
        return;
      }
      if (!this.uncertainCreate && (!this.ready || !!this.intent || !this.validPrice())) return;
      const button = this.q('[data-scout-submit]');
      this.submitting = true;
      button.disabled = true;
      this.phase('saving', this.t('saving'));
      button.textContent = this.t('saving');
      let pending;
      try {
        if (this.preview) { this.say(this.t('preview_draft_saved')); return; }
        const remember = this.form.elements.remember_interest.checked && window.ThryftScoutAccount.preferencesState() === true;
        const payload = this.uncertainCreate?.payload || {
          ...this.criteria(), product_id: this.config.product_id, variant_id: this.config.variant_id,
          country: this.config.country, request_id: this.requestId, permission: true,
          nonce: this.account.nonce, contract: 'once-shelf-v1', remember_interest: !!remember, personalization_privacy_allowed: !!remember
        };
        pending = this.uncertainCreate || { payload, at: this.accountFetchedAt || Date.now() };
        const response = await this.api('/watches', payload);
        if (!response.watch?.id || !['active', 'paused', 'stopped', 'completed'].includes(response.watch.status)) throw Error(this.t('unavailable'));
        this.uncertainCreate = null;
        if (this.isConnected) this.result(response.watch);
        else this.savedWatch = response.watch;
      } catch (error) {
        if ((!error.status || error.status >= 500) && pending) this.uncertainCreate = pending;
        if (error.status === 403 && error.message === EXPIRED_SESSION) {
          if (this.uncertainCreate) this.uncertainCreate.held = true;
          else { this.accountFetchedAt = 1; this.ready = false; }
        }
        this.phase('error', this.uncertainCreate ? this.t(this.uncertainCreate.held ? 'submission_hold' : 'submission_uncertain') : error.message);
        this.q('[data-scout-retry]').hidden = !!this.uncertainCreate;
      } finally {
        this.submitting = false; this.form.inert = false;
        button.disabled = !this.ready || !!this.intent || !this.validPrice();
        button.textContent = this.t(this.uncertainCreate ? 'submission_retry' : 'create');
        if (this.uncertainCreate) {
          for (const name of ['scope', 'condition', 'max_price', 'media_condition', 'sleeve_condition', 'remember_interest']) {
            const field = this.form.elements[name];
            if (field && 'disabled' in field) field.disabled = true;
            else if (field) Array.from(field).forEach(input => { input.disabled = true; });
          }
          button.disabled = !!this.uncertainCreate.held || Date.now() - this.uncertainCreate.at >= SESSION_AGE;
        }
        if (!this.uncertainCreate && this.sessionStale() && this.isConnected && this.dialog.open) await this.refresh({ review: true });
      }
    }
  }
  customElements.define('thryft-scout', Scout);
})();
